import { filter, isArray, isEmpty, isNumber, isString, map } from 'lodash'
import findIndex from 'lodash/findIndex'
import { action, computed, observable } from 'mobx'
import { MoreFilterUIStore } from '../moreFilterPopup/store/MoreFilterUIStore'
import { HttpService } from '@services/HttpService'
import { storage } from '@services/storageService'
import { getProductAttributeValue } from '@utils/get-product-attribute-value'
import { stores } from '@stores'

export class CNFProductFilterStore extends MoreFilterUIStore {
  private httpService = new HttpService()
  @observable filterAttributes
  @observable filterValues
  @observable aggregateAttributes
  @observable expanded = []
  @observable selected = {}
  @observable isShowSelected: boolean = false
  @observable excludedFilters = {}
  @observable filterSearchKey = ''

  @action setFilterAttributesAndValues = async (
    filterAttributes,
    filterValues,
    aggregateAttributes
  ) => {
    this.filterAttributes = filterAttributes
    this.aggregateAttributes = aggregateAttributes
    this.filterValues = filterValues
  }

  @action handleFilterSelection = (checked, filterValue, filterId) => {
    const selectedFilterOptions = { ...this.selected }
    const excludedFilterOptions = { ...this.excludedFilters }
    if (checked) {
      if (!selectedFilterOptions[filterId]) {
        selectedFilterOptions[filterId] = []
      }
      selectedFilterOptions[filterId]?.push(filterValue)

      const alreadyExcludedIndex = findIndex(
        excludedFilterOptions[filterId],
        (excluded: any) => excluded?.value === filterValue?.value
      )
      alreadyExcludedIndex !== -1 &&
        excludedFilterOptions[filterId]?.splice(alreadyExcludedIndex, 1)
      if (
        isEmpty(excludedFilterOptions[filterId]) ||
        !excludedFilterOptions[filterId]?.length
      ) {
        delete excludedFilterOptions[filterId]
      }
    } else {
      const alreadyCheckedFilterIndex = findIndex(
        selectedFilterOptions[filterId],
        (selectedFilter: any) => selectedFilter?.value === filterValue?.value
      )
      alreadyCheckedFilterIndex !== -1 &&
        selectedFilterOptions[filterId]?.splice(alreadyCheckedFilterIndex, 1)
      if (
        isEmpty(selectedFilterOptions[filterId]) ||
        !selectedFilterOptions[filterId]?.length
      ) {
        delete selectedFilterOptions[filterId]
      }
    }
    this.selected = { ...selectedFilterOptions }
    this.excludedFilters = { ...excludedFilterOptions }
  }

  @action handleFilterExclusion = (filterValue, filterId) => {
    const excludedFilterOptions = { ...this.excludedFilters }
    const selectedFilterOptions = { ...this.selected }
    const alreadySelectedFilterIndex = findIndex(
      selectedFilterOptions[filterId],
      (selectedFilter: any) => selectedFilter?.value === filterValue?.value
    )
    alreadySelectedFilterIndex !== -1 &&
      selectedFilterOptions[filterId]?.splice(alreadySelectedFilterIndex, 1)
    if (
      isEmpty(selectedFilterOptions[filterId]) ||
      !selectedFilterOptions[filterId]?.length
    ) {
      delete selectedFilterOptions[filterId]
    }
    if (!excludedFilterOptions[filterId]) {
      excludedFilterOptions[filterId] = []
    }
    const alreadyExcludedIndex = findIndex(
      excludedFilterOptions[filterId],
      (excludedFilter: any) => excludedFilter?.value === filterValue?.value
    )
    if (alreadyExcludedIndex === -1) excludedFilterOptions[filterId]?.push(filterValue)
    else {
      excludedFilterOptions[filterId]?.splice(alreadyExcludedIndex, 1)
      if (
        isEmpty(excludedFilterOptions[filterId]) ||
        !excludedFilterOptions[filterId]?.length
      ) {
        delete excludedFilterOptions[filterId]
      }
    }
    this.excludedFilters = { ...excludedFilterOptions }
    this.selected = { ...selectedFilterOptions }
  }

  @action clearFilter = filterId => {
    const selectedFilter = this.selected
    delete selectedFilter[filterId]
    const excludedFilter = this.excludedFilters
    delete excludedFilter[filterId]
    this.selected = { ...selectedFilter }
    this.excludedFilters = { ...excludedFilter }
  }

  @action clearAllFilter = () => {
    this.selected = {}
    this.excludedFilters = {}
  }

  @action handleShowSelected = event => {
    this.isShowSelected = event?.target?.checked
  }

  @action handleFilterExpansion = id => {
    if (this.expanded.includes(id)) {
      this.expanded = filter(this.expanded, ele => ele !== id)
    } else {
      this.expanded.push(id)
    }
  }

  @action handleAllFilterExpansion = () => {
    if (this.expanded.length) this.expanded = []
    else this.expanded = map(this.filterAttributes, 'id')
  }

  @action onFilterSearchKeyChange = key => {
    this.filterSearchKey = key
  }

  @action getSearchedData = (data, searchKey) => {
    const {
      productConfiguration: { defaultValuesFromConfig, findSpecificAttribute }
    } = stores
    if (!isArray(data)) {
      let searchedData = {}
      for (const key in data) {
        try {
          if (data.hasOwnProperty(key) && data[key]) {
            if (isString(data[key])) {
              return data[key]
                ?.toString()
                .toLowerCase()
                .includes(searchKey?.toLowerCase())
            } else if (isArray(data[key])) {
              const attributeObj = findSpecificAttribute(key)
              const initialValue = filter(data[key], element =>
                element.value?.toString().toLowerCase().includes(searchKey?.toLowerCase())
              )
              const values =
                initialValue.length > 0
                  ? initialValue
                  : data[key].filter(element => {
                      let nodeText = element.value
                      if (attributeObj) {
                        nodeText = getProductAttributeValue(
                          { name: nodeText },
                          attributeObj,
                          true,
                          defaultValuesFromConfig
                        )
                      }
                      return nodeText
                        ?.toString()
                        .toLowerCase()
                        .includes(searchKey?.toLowerCase())
                    })
              if (values.length > 0) {
                searchedData[key] = values
              }
            }
          } else return false
        } catch (err) {
          console.error('Error in searching data')
          console.error(err)
        }
      }
      return searchedData
    } else {
      return filter(data, filter => {
        try {
          return Object.keys(filter).some(key => {
            if (filter.hasOwnProperty(key)) {
              if (isString(filter[key]) || isNumber(filter[key])) {
                return filter[key]
                  .toString()
                  .toLowerCase()
                  .includes(searchKey.toLowerCase())
              }
              return false
            }
            return false
          })
        } catch (err) {
          console.error('Error in searching data')
          console.error(err)
        }
      })
    }
  }

  @computed get totalFilterSelectionCount() {
    return Object.keys(this.selected).reduce((acc, filterKey) => {
      acc = acc + this.selected[filterKey]?.length
      return acc
    }, 0)
  }

  @action resetFilterSearch = () => {
    this.filterSearchKey = ''
    this.expanded = []
  }

  fetchFilterAttributeValues = (
    url: string,
    requestBody,
    filterAttributes,
    aggregateAttributes
  ) => {
    const accessToken: string = storage.getItem('access_token')
    const options = {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json',
      'Content-Type': 'application/json'
    }
    return this.httpService
      .post(url, requestBody, {
        headers: options
      })
      .then(
        res => {
          this.setFilterAttributesAndValues(
            filterAttributes,
            res.data || {},
            aggregateAttributes
          )
        },
        e => {
          console.error(e)
          throw new Error('bb.settings.productCardConfigSettings.fetchAttributes')
        }
      )
  }

  resetAllFilterData = () => {
    this.filterAttributes = []
    this.filterValues = {}
    this.aggregateAttributes = []
    this.expanded = []
    this.selected = {}
    this.isShowSelected = false
    this.excludedFilters = {}
    this.filterSearchKey = ''
  }
}
